<template>
    <div class="flex flex-col items-center gap-4 text-center py-2">
        <span class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-chip-status-done-bg text-chip-status-done-text text-[32px]" aria-hidden="true">
            <i class="bi bi-check-lg" aria-hidden="true"></i>
        </span>
        <h2 class="display-serif text-ink">{{ t("reflections.self.complete.title") }}</h2>
        <p class="body-small text-ink-muted max-w-[460px]">{{ lede }}</p>

        <BaseCard padding="md" class="w-full max-w-[460px] mt-3 flex items-start gap-3 text-left">
            <ReflectionIcon kind="sixty" size="md" class="mt-1" />
            <div class="flex flex-col gap-1 min-w-0">
                <span class="scaffold-body-small text-ink-muted">{{ t("reflections.self.complete.whatsNext") }}</span>
                <span class="body-semibold text-ink">{{ t("reflections.self.complete.stillCollecting") }}</span>
                <span class="body-small text-ink-muted">{{ whatsNext }}</span>
            </div>
        </BaseCard>

        <div class="flex items-center justify-center gap-2 flex-wrap mt-3">
            <BaseButton theme="neutral" size="sm" @click="emit('back')">{{ t("reflections.self.complete.backToUpcoming") }}</BaseButton>
            <BaseButton theme="primary" size="sm" @click="emit('view')">{{ t("reflections.self.complete.viewMy360") }}</BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleResponseSummary } from "~vue/api/generated/reflections";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import { computed, inject, onMounted, type Ref, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    cycleId: string;
}

const { cycleId } = defineProps<Props>();
const emit = defineEmits<{ back: []; view: [] }>();

const { t } = useI18n();
const userDetails = inject<Ref<UserDetails>>("user_details");
const summary = ref<CycleResponseSummary | null>(null);

const lede = computed(() => {
    const firstName = userDetails?.value?.first_name;
    return firstName ? t("reflections.self.complete.ledeNamed", { name: firstName }) : t("reflections.self.complete.ledeAnonymous");
});

const whatsNext = computed(() => {
    if (!summary.value || summary.value.total === 0) {
        return t("reflections.self.complete.ratersPending");
    }
    return t("reflections.self.complete.ratersResponded", { responded: summary.value.responded, total: summary.value.total });
});

onMounted(async () => {
    const { data } = await useFetch(`/v1/reflections/cycles/${cycleId}`).json<CycleEnvelopeResponse>();
    summary.value = data.value?.cycle.response_summary ?? null;
});
</script>
